<template>
    <v-flex class="community-block">
        <div v-infinite-scroll="getCommunityDirectoryResult"
             :infinite-scroll-distance="2000"
             class="relative-position">
            <status-overlay class="py-5 directory_overlay" :action="initialResultsAction"></status-overlay>
            <div v-if="communitiesList && communitiesList.length > 0">
                <v-layout
                        row
                        v-for="(community, index) in communitiesList"
                        :key="community.id"
                        class="community-tile">
                    <v-flex class="community-tile__image relative-position" pr-4>
                        <figure>
                            <img :key="community.communityImageURL"
                                 :src="community.communityImageURL"
                                 :onerror="imageErrorFallback(community.communityFallBackImageURL)"
                                 :data-qe-id="`community-search-result_${index}_image_forward_link`"
                                 @click="openCommunity(community)"
                            >
                        </figure>
                        <span class="community-tile__private"
                              :data-qe-id="`community-search-result_${index}_locked_icon`"
                              v-if="isPrivateCommunity(community)">
                                    <i class="fas fa-lock"></i>
                            </span>
                    </v-flex>
                    <v-flex>
                        <v-layout
                                column
                                wrap>
                            <v-flex class="community-tile__name link-element">
                                <span @click="openCommunity(community)"
                                   :data-qe-id="`community-search-result_${index}_community-name`">
                                    {{community.communityName}}
                                </span>
                            </v-flex>
                            <v-flex class="community-tile__type uppercase"
                                    :data-qe-id="`community-search-result_${index}_community-type`">
                                {{community.communityType}}
                            </v-flex>
                            <v-layout column class="community-tile__participants--wrapper">
                                <v-flex class="community-tile__participants"
                                        v-if="community.participants && community.participants > 0"
                                        :data-qe-id="`community-search-result_${index}_community-participant`">
                                    {{`${communityDirectoryPageConstant.label.PARTICIPANTS}  ${community.participants}`}}
                                </v-flex>
                                <v-flex class="community-tile__focus"
                                        :data-qe-id="`community-search-result_${index}_community-focus`">
                                    {{community.communityFocus}}
                                </v-flex>
                            </v-layout>
                            <v-flex class="community-tile__eligibility"
                                    v-if="isPrivateCommunity(community)"
                                    :data-qe-id="`community-search-result_${index}_community-eligibility`">
                                {{communityDirectoryPageConstant.label.MEMBERS_ONLY}}
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
                <v-layout slot="after-container" class="loading-next-status">
                    <status-overlay :action="nextResultsAction" content-height="100px"></status-overlay>
                </v-layout>
            </div>

            <v-layout
                    column
                    v-if="communitiesList && communitiesList.length === 0"
                    pl-0
                    pt-3
                    class="no-result">
                <v-flex v-html="noResultsFoundMessage.labelA" pb-1 class="no-result__label1"></v-flex>
                <v-flex class="no-result__label2">
                    <span v-html="noResultsFoundMessage.labelB"></span>
                    <span v-if="noResultsFoundMessage.labelC">{{noResultsFoundMessage.labelC}}</span>
                    <span class="no-result__directory-link"
                          v-if="noResultsFoundMessage.labelC"
                          data-qe-id="view_full_member_directory_link"
                          @click="loadFullDirectory">
                        {{communityDirectoryPageConstant.label.VIEW_FULL_COMMUNITY_DIRECTORY}}
                    </span>
                </v-flex>
            </v-layout>
        </div>
    </v-flex>
</template>

<script>
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import { storeAction } from "../../store/storeActions.constant";
    import { storeGetters } from "../../store/storeGetters.constant";
    import StatusOverlay from '@/components/StatusOverlay/StatusOverlay';
    import { imageErrorFallback } from "../../common/utils/utils";
    import { communityDirectoryPageConstant } from "./communityDirectoryPage.constants";
    import { getNoResultStatement } from "../MemberDirectoryPage/memberDirectoryPage.constants";
    import { appConstant } from "../../app.constant";
    import { storeMutation } from "../../store/storeMutations.constant";
    import { getHbRedirectByCommunityId } from "@/common/utils/hivebriteUtils";
    import { gaDataLayerEvent } from "@/plugins/analytics";
    import { gtmEventName } from "@/plugins/gtmAnalytics";

    export default {
        name: "CommunitySearchResults",
        data() {
            return {
                communityDirectoryPageConstant,
            };
        },
        components: {
            StatusOverlay,
        },
        methods: {
            ...mapActions({
                getCommunityDirectoryResults: `communityDirectory/${storeAction.getInitialCommunityDirectorySearchResults}`,
                getNextCommunityDirectoryResults: `communityDirectory/${storeAction.getNextCommunityDirectorySearchResults}`,
            }),
            ...mapMutations({
                setCommunityDirectorySearchText: `communityDirectory/${storeMutation.setCommunityDirectorySearchText}`,
            }),
            imageErrorFallback,
            getCommunityDirectoryResult() {
                if (!this.disableCommunityDirectoryInfiniteScroll) {
                    if (!this.nextCommunityDirectorySearchOffset) {
                        this.getCommunityDirectoryResults();
                    } else {
                        this.getNextCommunityDirectoryResults();
                    }
                }
            },
            openCommunity(community) {
              const hbRedirectCommunity = getHbRedirectByCommunityId(this.connectPlusRedirects, community.id);
              if (hbRedirectCommunity) {
                window.open(hbRedirectCommunity.redirectUrl, '_self');
              } else {
                window.open(community.navigationUrl, '_parent');
              }
              gaDataLayerEvent(gtmEventName.C_Community_Directory_Selected_Community, `${community.communityType} | ${community.id}`);
            },
            loadFullDirectory() {
                this.setCommunityDirectorySearchText('');
                sessionStorage.removeItem(appConstant.storageKeys.communityDirectoryStoredFilters);
                this.$router.push({
                    path: "/communityDirectoryPage",
                });
            },
            isPrivateCommunity(community) {
                return community.communityCategory && community.communityCategory === communityDirectoryPageConstant.label.PRIVATE;
            },
        },
        computed: {
            ...mapState('actionState', ['actions']),
            ...mapGetters({
                communityDirectoryResults: `communityDirectory/${storeGetters.communityDirectoryResults}`,
                communityState: `communityDirectory/${storeGetters.communityState}`,
                communitySearchText: `communityDirectory/${storeGetters.communityDirectorySearchText}`,
                communitySelectedFilters: `communityDirectory/${storeGetters.communitySelectedFilters}`,
                disableCommunityDirectoryInfiniteScroll: `communityDirectory/${storeGetters.disableCommunityDirectoryInfiniteScroll}`,
                nextCommunityDirectorySearchOffset: `communityDirectory/${storeGetters.nextCommunityDirectorySearchOffset}`,
                isMobileAppBrowser: `${[storeGetters.isMobileAppBrowser]}`,
                connectPlusRedirects: `connectPlus/${storeGetters.connectPlusRedirects}`,
            }),
            initialResultsAction() {
                return this.actions[storeAction.getInitialCommunityDirectorySearchResults];
            },
            nextResultsAction() {
                return this.actions[storeAction.getNextCommunityDirectorySearchResults];
            },

            communitiesList() {
                return this.communityDirectoryResults && this.communityDirectoryResults.searchResults
                    ? this.communityDirectoryResults.searchResults :
                    [];
            },
            noResultsFoundMessage() {
                return getNoResultStatement(this.communitySearchText, (this.communitySelectedFilters || []), this.$route.name);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../common/styles/_colors.scss';
    @import '../../common/styles/_responsive.scss';

    .community-tile {
        font-family: 'Noto Sans', sans-serif !important;
        padding: 20px 5px 5px 15px;
        transition: 0.3s all ease;
        height: 176px;
        border-bottom: 1px solid rgba($ypo-gray, 0.5);
        cursor: pointer;
        position: relative;

        &:hover {
            background: rgba($ypo-cta, 0.09);
        }

        .flex {
            flex: 0 1 auto !important;
        }

        &__image {

            figure {
                height: 114px;
                border-radius: 50%;
                width: 114px;
                overflow: hidden;
                text-align: center;
                border: 4px solid $ypo-open-blue-gray;

                img {
                    max-width: 100%;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }
        }

        &__private {
            width: 33px;
            height: 30px;
            position: absolute;
            left: 85px;
            top: 55%;
            z-index: 1;
            color: $ypo-gold;

            @include breakpoint(xs) {
                left: 65px;
                top: 55px;
            }

            i {
                font-size: 18px;
                text-shadow: -1px 0 $white, 0 1px $white, 1px 0 $white, 0 -1px $white;

                @include breakpoint(xs) {
                    font-size: 14px;
                }
            }
        }

        &__name {
            font-weight: bold;
            color: $ypo-cta;
            text-overflow: ellipsis;
            font-size: 18px;
            white-space: nowrap;
            overflow: hidden;

            &:hover {
                text-overflow: clip;
                white-space: normal;
                text-align: left;
            }

            a {
                text-decoration: none;
                color: $ypo-cta;
            }

            @include breakpoint(xs) {
                font-size: 16px;
            }

        }

        &__type {
            color: $ypo_blue;
            font-size: 14px;
            margin-top: -3px;

        }

        &__participants {
            color: $ypo-black-opac;
            font-size: 14px;

            &--wrapper {
                padding-top: 15px;
            }
        }

        &__focus {
            color: $ypo-black-opac;
            font-size: 14px;
        }

        &__eligibility {
            color: $ypo-black-opac;
            font-size: 14px;
            padding-top: 10px;

            @include breakpoint(xs) {
                padding-top: 5px;
            }
        }

        @include breakpoint(xs) {
            border: 1px solid rgba($ypo-gray, 0.5);
            margin: 3px 10px;
            height: 160px;
            border-radius: 3px;

            &__type,
            &__focus {
                font-size: 14px;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            &__image {
                left: 7px;

                figure {
                    height: 80px;
                    width: 80px;
                    border: 3px solid $ypo-open-blue-gray;
                }
            }
        }
    }

    .loading-next-status {
        position: relative;
        min-height: 100px;
    }

    .no-result {
        font-family: 'Noto Sans', sans-serif;

        &__label1 {
            font-size: 18px;
            font-weight: 600;
        }

        &__label2 {
            font-size: 15px;
        }

        &__directory-link {
            color: $ypo-cta;
            font-weight: 600;
            cursor: pointer;
        }
    }

</style>
